/* ══════════════════════════════════════════════════════════════
   <product-scroller> — sections/product-scroller.liquid
   Horizontal track that drifts on its own and stops the moment anyone
   touches, hovers, drags or focuses it. The item list is rendered twice
   so the loop wraps seamlessly (rewinding by half the track width lands
   on an identical frame).

   The animation frame loop only runs while the track can actually move:
   it stops while paused, off screen or in a background tab, and starts
   again when that changes.
   ══════════════════════════════════════════════════════════════ */
(function () {
  if (customElements.get('product-scroller')) return;

  class ProductScroller extends HTMLElement {
    connectedCallback() {
      var root = this.querySelector('[data-psc]');
      if (!root) return;
      var viewport = root.querySelector('[data-psc-viewport]');
      var track = root.querySelector('[data-psc-track]');
      if (!viewport || !track) return;

      this.ac = new AbortController();
      var signal = this.ac.signal;

      var autoplay = root.getAttribute('data-autoplay') === 'true';
      var speed = parseFloat(root.getAttribute('data-speed')) || 30; /* px per second */
      var RESUME = parseFloat(root.getAttribute('data-resume'));
      if (isNaN(RESUME)) RESUME = 300;
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      var paused = false;
      var offscreen = false;   /* from the IntersectionObserver */
      var tabHidden = document.hidden;
      var rafId = null;
      var lastTime = null;
      var resumeTimer = null;
      var syncing = false;

      /* Position is tracked as a float rather than read back from scrollLeft
         each frame: at a slow drift the per-frame step is under a pixel and
         browsers round scrollLeft on read, so the track would never move. */
      var pos = 0;

      function halfWidth() { return track.scrollWidth / 2; }
      function canMove() { return autoplay && !reduced && !paused && !offscreen && !tabHidden; }

      function tick(now) {
        if (!canMove()) { rafId = null; return; }
        if (lastTime === null) lastTime = now;
        var dt = (now - lastTime) / 1000;
        lastTime = now;
        if (dt > 0.1) dt = 0.1;  /* no leap after a long gap */

        var half = halfWidth();
        pos += speed * dt;
        if (half > 0 && pos >= half) pos -= half;

        syncing = true;
        viewport.scrollLeft = pos;
        syncing = false;

        rafId = window.requestAnimationFrame(tick);
      }

      function run() {
        if (rafId !== null || !canMove()) return;
        lastTime = null;
        rafId = window.requestAnimationFrame(tick);
      }

      function pause() {
        paused = true;
        clearTimeout(resumeTimer);
      }

      function resumeSoon(delay) {
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(function () {
          /* Pick up from wherever the user left it, not where the drift was. */
          pos = viewport.scrollLeft;
          lastTime = null;
          paused = false;
          run();
        }, delay === undefined ? RESUME : delay);
      }

      var opts = { signal: signal };
      var passive = { passive: true, signal: signal };
      root.addEventListener('mouseenter', pause, opts);
      root.addEventListener('mouseleave', function () { resumeSoon(); }, opts);
      root.addEventListener('pointerdown', pause, passive);
      root.addEventListener('touchstart', pause, passive);
      ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach(function (ev) {
        root.addEventListener(ev, function () { resumeSoon(); }, passive);
      });
      viewport.addEventListener('wheel', function () { pause(); resumeSoon(); }, passive);

      viewport.addEventListener('scroll', function () {
        if (syncing) return;
        var half = halfWidth();
        if (half > 0) {
          if (viewport.scrollLeft >= half) viewport.scrollLeft -= half;
          else if (viewport.scrollLeft <= 0) viewport.scrollLeft += half;
        }
        pos = viewport.scrollLeft;
      }, passive);

      root.addEventListener('focusin', pause, opts);
      root.addEventListener('focusout', function () { resumeSoon(400); }, opts);

      document.addEventListener('visibilitychange', function () {
        tabHidden = document.hidden;
        if (!tabHidden) { pos = viewport.scrollLeft; run(); }
      }, opts);

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            offscreen = !entry.isIntersecting;
            if (entry.isIntersecting) { pos = viewport.scrollLeft; run(); }
          });
        }, { threshold: 0 });
        io.observe(root);
        signal.addEventListener('abort', function () { io.disconnect(); });
      }

      function cardStep() {
        var card = track.querySelector('.psc__card');
        if (!card) return 240;
        var styles = window.getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      var prev = root.querySelector('[data-psc-prev]');
      var next = root.querySelector('[data-psc-next]');
      if (prev) prev.addEventListener('click', function () {
        pause();
        viewport.scrollBy({ left: -cardStep(), behavior: 'smooth' });
        resumeSoon(1200);
      }, opts);
      if (next) next.addEventListener('click', function () {
        pause();
        viewport.scrollBy({ left: cardStep(), behavior: 'smooth' });
        resumeSoon(1200);
      }, opts);

      signal.addEventListener('abort', function () {
        clearTimeout(resumeTimer);
        if (rafId !== null) window.cancelAnimationFrame(rafId);
        rafId = null;
      });

      /* Images load lazily, so the track width grows after first paint. */
      pos = viewport.scrollLeft;
      run();
      if (document.readyState !== 'complete') {
        window.addEventListener('load', function () { pos = viewport.scrollLeft; run(); }, { once: true, signal: signal });
      }
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
    }
  }

  customElements.define('product-scroller', ProductScroller);
})();
